"use client";
import { useLocaleCtx } from "@/contexts/locale";
import { filter } from "@/types/gallery";

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { getNominees } from "@/services/nominees/nominees.client";
import { Categories } from "@/types";
import { NomineesBeResponse } from "@/services/nominees/nominees.server";
import { getGalleryPageWinners } from "@/services/gallery/gallery.client";
import { Winner } from "@/app/[lang]/winners/type";

interface NomineesContextProps {
  nominees: NomineesBeResponse["data"] | [];
  loading: boolean;
  pageNo: number;
  pageSize: number;
  category: filter;
  subCategory: filter;
  setPageNo: (pageNo: number) => void;
  setCategory: Dispatch<SetStateAction<filter>>;
  setSubCategory: Dispatch<SetStateAction<filter>>;
  lang: "ar" | "en";
  categories: Categories[];
}

const NomineesContext = createContext<NomineesContextProps>({} as NomineesContextProps);

export function useNomineesCtx() {
  const context = useContext(NomineesContext);
  if (!context) {
    throw new Error("useNominees must be used within a NomineesProvider");
  }
  return context;
}

export function NomineesContextProvider({
  children,
  categories,
}: {
  children: ReactNode;
  categories: Categories[];
}) {
  const { lang } = useLocaleCtx();

  const [loading, setLoading] = useState(true);
  const [nominees, setNominees] = useState<NomineesBeResponse["data"] | []>([]);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(1000);
  const [category, setCategory] = useState<filter>(categories?.[0]);
  const [subCategory, setSubCategory] = useState<filter>({
    title: "all",
    value: "",
  });

  const transformNominees = (nominees: NomineesBeResponse["data"], winners: Winner[]) => {
    return nominees.map((nominee) => {
      const nomineeName = nominee.name.trim().toLowerCase();
      const nomineeCategory = nominee.nominee_category_lookup.title.trim().toLowerCase();

      const isWinner = winners.some((winner) => {
        const winnerName = winner.name.trim().toLowerCase();
        // Checking title to avoid additional honorees added to winners collection in Gallery page to throw error
        const winnerCategory = winner.winner_category_lookup?.title.trim().toLowerCase();
        console.log(winnerCategory);
        return nomineeName === winnerName && nomineeCategory === winnerCategory;
      });

      return { ...nominee, isWinner };
    });
  };

  const fetchMoreNominees = async () => {
    setLoading(true);

    const nomineeData = await getNominees({
      lang,
      pageNo,
      pageSize,
      category: category.value,
      subCategory: subCategory.value,
    });

    const galleryData = await getGalleryPageWinners({
      lang,
      pageNo: 1,
      pageSize,
      year: "2026",
      category: "",
      subCategory: "",
    });

    if (nomineeData && galleryData) {
      const transformedNominees = transformNominees(nomineeData?.data, galleryData?.data);

      setNominees(transformedNominees);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMoreNominees();
  }, [pageNo, lang, category, subCategory]);
  return (
    <NomineesContext.Provider
      value={{
        nominees,
        loading,
        pageNo,
        pageSize,
        category,
        subCategory,
        setPageNo,
        setCategory,
        setSubCategory,
        lang,
        categories,
      }}
    >
      {children}
    </NomineesContext.Provider>
  );
}
